import * as ss from 'superstruct';
import { OpenAiAdsAccountId } from '../../tracking/ad-account-id';

/**
 * OpenAI Conversions API event types we send. The app-only `app_installed`
 * and `app_opened` need a mobile app as the action source, which conversions
 * never have.
 */
export const OpenAiAdsEventTypes = [
  'lead_created',
  'appointment_scheduled',
  'registration_completed',
  'order_created',
  'checkout_started',
  'items_added',
  'contents_viewed',
  'page_viewed',
  'subscription_created',
  'trial_started',
  'custom',
] as const;

export const OpenAiAdsEventType = ss.enums(OpenAiAdsEventTypes);
export type OpenAiAdsEventType = (typeof OpenAiAdsEventTypes)[number];

/** Every standard event type, including the app-only ones we never send. */
const reservedEventNames: readonly string[] = [
  ...OpenAiAdsEventTypes,
  'app_installed',
  'app_opened',
];

/**
 * 1-64 letters, digits, `_` or `-`, starting and ending with a letter or
 * digit, and not a standard event type. OpenAI lowercases it.
 */
export const OpenAiAdsCustomEventName = ss.refine(
  ss.pattern(ss.string(), /^[a-z0-9](?:[a-z0-9_-]{0,62}[a-z0-9])?$/i),
  'OpenAiAdsCustomEventName',
  (value) =>
    !reservedEventNames.includes(value.toLowerCase()) ||
    `"${value}" is a standard OpenAI event type, so it can't be a custom event name.`,
);

/**
 * The configuration for sending conversions to OpenAI (ChatGPT) ads through
 * its Conversions API.
 *
 * https://developers.openai.com/ads/conversions-api
 */
export const OpenAiAdsOutputStreamDestination = ss.refine(
  ss.type({
    name: ss.literal('openai-ads'),
    /** Conversions API key, from Ads Manager's Conversions tab. Write-only. */
    apiKey: ss.string(),
    /** The data source's Pixel ID, which events are sent to. */
    pixelId: ss.string(),
    eventType: OpenAiAdsEventType,
    /** Required when `eventType` is `custom`. */
    customEventName: ss.optional(ss.nullable(OpenAiAdsCustomEventName)),
    /** ISO 4217 currency of conversion values. Defaults to USD. */
    currencyCode: ss.optional(
      ss.nullable(ss.pattern(ss.string(), /^[a-z]{3}$/i)),
    ),
    /** OpenAI ad account ID (`adacct_...`). */
    accountId: OpenAiAdsAccountId,
  }),
  'OpenAiAdsOutputStreamDestination',
  (value) =>
    value.eventType !== 'custom' ||
    !!value.customEventName ||
    'A custom event type needs a custom event name.',
);

export type OpenAiAdsOutputStreamDestination = ss.Infer<
  typeof OpenAiAdsOutputStreamDestination
>;
